/**
 * Admin OOC queue API layer (STORY-010-004).
 *
 * Types mirror the backend DTOs in
 * `apps/api/garden/features/admin_ooc/schemas.py`. The transition is a PATCH
 * (apiFetch adds the Idempotency-Key for mutating methods); the CSV export is
 * a Bearer-authenticated blob download.
 */
import { apiClient, apiFetch, getApiClientConfig } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";

// ─────────────────────────── Types ─────────────────────────────────────────

export interface OocListItem {
  id: string;
  status: string;
  language: string;
  region: string | null;
  normalized_query: string;
  intended_category_id: string | null;
  created_at: string;
}

export interface OocListResponse {
  items: OocListItem[];
  next_cursor: string | null;
}

export interface OocDetail {
  id: string;
  status: string;
  language: string;
  region: string | null;
  original_query: string;
  normalized_query: string;
  intended_category_id: string | null;
  wont_do_reason: string | null;
  resolved_by_admin_id: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  submitter_email: string | null;
}

export type OocStatus = "in_progress" | "resolved" | "wont_do";

export interface OocTransitionRequest {
  new_status: OocStatus;
  wont_do_reason?: string | null;
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const oocQueryKeys = {
  list: (filters: Record<string, unknown>) => ["admin", "ooc", "list", filters] as const,
  detail: (id: string) => ["admin", "ooc", "detail", id] as const,
} as const;

// ─────────────────────────── Fetchers ──────────────────────────────────────

export interface ListOocParams {
  status?: string;
  language?: string | null;
  region?: string | null;
  cursor?: string | null;
  limit?: number;
}

export function listOoc(params: ListOocParams): Promise<OocListResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.language) query.set("language", params.language);
  if (params.region) query.set("region", params.region);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<OocListResponse>(`/admin/ooc${qs ? `?${qs}` : ""}`);
}

export function getOoc(id: string): Promise<OocDetail> {
  return apiClient.get<OocDetail>(`/admin/ooc/${id}`);
}

export function transitionOoc(
  id: string,
  body: OocTransitionRequest,
): Promise<OocDetail> {
  return apiFetch<OocDetail, OocTransitionRequest>({
    method: "PATCH",
    path: `/admin/ooc/${id}/status`,
    body,
  });
}

/** Bearer-authenticated CSV download (the export URL needs the JWT header). */
export async function exportOocCsv(status = "in_progress"): Promise<void> {
  const { baseUrl } = getApiClientConfig();
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${baseUrl}/admin/ooc/export.csv?status=${status}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`export failed: ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "ooc-requests.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
